import { Box } from "@mui/material";
import { IconTrendingUp } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gameDesc from "../../../../assets/images/home/game-desc.png";
import gameMob from "../../../../assets/images/home/game-mob.png";
import payout from "../../../../assets/images/home/payout.png";
import playerJoined from "../../../../assets/images/home/player-joined.png";
import Button from "../../../../components/ui/Button";
import Text from "../../../../components/ui/Text";

export const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.4,
        rootMargin: "0px 0px -50px 0px",
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return (
    <Box
      ref={sectionRef}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "center",
        height: { md: "100dvh" },
        padding: { xs: "48px 0 0 0", md: 0 },
        backgroundColor: "#F2F4F8",
      }}
    >
      <Box
        sx={{
          width: { md: "50%" },
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "center", md: "flex-end" },
        }}
      >
        <Box
          sx={{
            maxWidth: 600,
            px: 2,
            pt: { xs: "20px", md: "40px" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Text
            variant="pageHeader"
            component="h1"
            sx={{
              fontSize: { xs: 28, md: 38, lg: 54 },
              fontWeight: 900,
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            Fill your sports games fast. No more chasing players.
          </Text>
          <Text
            sx={{
              fontSize: 16,
              lineHeight: 1.5,
              pr: { md: 6 },
            }}
          >
            Findaside helps organisers post games, manage signups, and keep
            games full - all in one place.
          </Text>
          <Box
            sx={{
              mt: { xs: 2, md: 8 },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Button
              component={Link}
              to="/create-game"
              sx={{
                fontWeight: 500,
                fontSize: { xs: 18, md: 16, lg: 20 },
                height: { xs: 56, md: 48, lg: 56 },
                width: { xs: "100%", md: "fit-content" },
                padding: { xs: "0 20px", lg: "0 32px" },
                opacity: isVisible ? 1 : 0,
                transition: "all 0.8s ease-out",
              }}
            >
              Launch your game for free
            </Button>
            <Button
              variant="custom"
              component={Link}
              to="/upcoming"
              sx={{
                fontWeight: 500,
                fontSize: { xs: 18, md: 16, lg: 20 },
                width: { xs: "100%", md: "fit-content" },
                backgroundColor: "transparent",
                color: "#1890FF",
                border: "2px solid #1890FF",
                padding: { xs: "0 20px", lg: "0 32px" },
                height: { xs: 56, md: 48, lg: 56 },
                "&:hover": {
                  border: "2px solid #40A9FF",
                },
                opacity: isVisible ? 1 : 0,
                transition: "all 0.8s ease-out 0.3s",
              }}
            >
              I'm looking to play
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-start" },
              alignItems: "center",
              borderRadius: "40px",
              px: { md: 7 },
              py: 1,
              mt: 3,
              gap: 2,
              backgroundColor: "#D2F0FF",
              width: { xs: "auto", md: "fit-content" },
              opacity: isVisible ? 1 : 0,
              transition: "all 0.8s ease-out 0.6s",
            }}
          >
            <IconTrendingUp size={22} />
            <Text
              sx={{
                fontSize: 16,
                lineHeight: 1.5,
              }}
            >
              +1000 games launched
            </Text>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: { md: "50%" },
          aspectRatio: { xs: 1.1, md: "auto" },
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          paddingBottom: { xs: 6, md: 0 },
        }}
      >
        <Box>
          <img
            src={gameDesc}
            alt="Hero Illustration"
            style={{
              marginRight: "auto",
              width: "100%",
              maxWidth: "900px",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translate(8%, 5%)" : "translate(20%, 5%)",
              transition: "all 1s ease-in-out 0.2s",
            }}
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: { xs: 32, md: 0 },
            transform: isVisible
              ? "translate(5%, 18%)"
              : { xs: "translate(100vw, 18%)", md: "translate(50vw, 18%)" },
            transition: "all 0.8s ease-in-out 0.6s",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: { xs: "32vw", md: "17vw" },
              maxWidth: 260,
              aspectRatio: 0.488,
              maxHeight: "70vh",
            }}
          >
            <img
              src={gameMob}
              alt="Hero Illustration"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: { xs: 32, md: 0 },
            opacity: isVisible ? 1 : 0,
            transform: "translate(67%, 220%)",
            transition: "all 0.6s ease-in-out 1.5s",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: { xs: "32vw", md: "17vw" },
              maxWidth: 250,
              aspectRatio: 1.716,
              maxHeight: "18vh",
            }}
          >
            <img
              src={payout}
              alt="Hero Illustration"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: { xs: 32, md: 0 },
            opacity: isVisible ? 1 : 0,
            transform: "translate(-6%, 90%)",
            transition: "all 0.6s ease-in-out 1.8s",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: { xs: "30vw", md: "14vw" },
              maxWidth: 220,
              aspectRatio: 2.77,
            }}
          >
            <img
              src={playerJoined}
              alt="Hero Illustration"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
